import { Tables, TablesInsert } from '@/types/database.types';
import { CustomMutationOptions, CustomQueryOptions } from '@/types/query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createWorkspace,
  deleteWorkspace,
  getWorkspaceById,
  getWorkspaces,
  updateWorkspaceById,
} from '.';
import { useParams } from 'react-router-dom';
import { RouteParams } from '@/router/type';
import {
  CreateWorkspaceParams,
  DeleteWorkspaceParams,
  UpdateWorkspaceParams,
} from './type';
import useParamWorkspaceId from '@/hook/useParamWorkspaceId';

export const useGetWorkspaces = (
  option?: CustomQueryOptions<Tables<'workspace'>[]>
) => {
  return useQuery({
    queryKey: ['workspace'],
    queryFn: getWorkspaces,
    ...option,
  });
};

export const useGetWorkspaceById = (
  { id }: { id: string },
  option?: CustomQueryOptions<Tables<'workspace'>>
) => {
  return useQuery({
    queryKey: ['workspace', { id }],
    queryFn: async () => {
      const data = await getWorkspaceById(id || '');
      return data;
    },
    ...option,
  });
};

export const useGetCurrentWorkspace = (
  option?: CustomQueryOptions<Tables<'workspace'>>
) => {
  const { workspaceId } = useParamWorkspaceId();
  return useQuery({
    queryKey: ['workspace', { id: workspaceId }],
    queryFn: async () => {
      const data = await getWorkspaceById(workspaceId || '');
      return data;
    },
    ...option,
  });
};

export const useCreateWorkspace = (
  options?: CustomMutationOptions<CreateWorkspaceParams>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createWorkspace,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['workspace'],
      });
    },
    ...options,
  });
};

export const useDeleteWorkspace = (
  options?: CustomMutationOptions<DeleteWorkspaceParams>
) => {
  return useMutation({
    mutationFn: deleteWorkspace,
    ...options,
  });
};

export const useUpdateWorkspace = (
  options?: CustomMutationOptions<UpdateWorkspaceParams>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateWorkspaceById,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['workspace'],
      });
    },
    ...options,
  });
};
